<script setup lang="ts">
const priorities = [
  {
    number: '01',
    title: '付款资料',
    level: '重中之重',
    descriptions: [
      '使用您本人在可靠的实体银行办理的银行卡进行付款。慎用虚拟卡、交易所卡。不要使用各类代充服务。',
      '卡片归属国家、AI 网站留存的账单地址，以及注册和第一次连接时选择的线路所在地区要匹配。',
    ],
  },
  {
    number: '02',
    title: '账户信息',
    level: '重点关注',
    descriptions: [
      '自己注册 AI 网站账号去使用。避免使用第三方提供的成品账号。',
      '优先使用自己常用的国外企业邮箱，也可以用 Proton Mail。避免使用未绑定全套外国资料的 Gmail 邮箱。',
    ],
  },
  {
    number: '03',
    title: '设备环境',
    level: '细心调试',
    descriptions: ['根据付款资料、账户资料和所选线路，去设置电脑或手机的时区、地区和语言，做到一一对应。'],
  },
  {
    number: '04',
    title: '使用习惯',
    level: '慎重对待',
    descriptions: [
      '设置好 PandaFan 和 AI 软件或网页后，尽量保持不变。避免短时间内不断切换不同国家的线路、修改浏览器和软件设置。',
      '做到专人专号，不与他人分享 AI 账号。避免异地登录，避免一次性登录过多设备，避免在多台电脑中频繁切换账号。',
      '在 AI 服务条款范围内合理使用。',
    ],
  },
] as const

const setupSteps = [
  {
    titleParts: ['选择合适的线路，', '并开启增强模式'],
    compactTitle: false,
    description: '先看 AI 平台支持哪些国家和地区，选择合适的线路，并在 PandaFan 客户端内开启增强模式。手机默认是增强模式。不必一直固定同一个 IP，但连接的国家尽量保持一致，不要一天换好几个国家。',
  },
  {
    titleParts: ['设置时区、地区和语言'],
    compactTitle: false,
    description: '把系统时区、地区和语言设成与服务器所在地区一致。比如连接美国服务器，就选择美国地区、相应时区和英语。设置好后不要轻易修改。',
  },
  {
    titleParts: ['PandaFan 可以保持智能分流'],
    compactTitle: true,
    description: 'Claude、Codex、ChatGPT 等常见 AI 平台会用到的地址，PandaFan 已经持续收录。智能分流模式就能正常使用。全局代理不是必须开启，有需要时再切换。',
  },
  {
    titleParts: ['账号自己注册、', '自己合理使用'],
    compactTitle: false,
    description: '优先使用自己常用的国外企业邮箱，也可以用 Proton Mail。Gmail 邮箱因为风险关联，如未绑定全套海外资料也很容易被误伤。不要用 QQ、163 等中国大陆邮箱，会明显增加风控概率。如需使用手机号，不要用一次性的号码。账号、邮箱和手机号都要自己注册、自己保管，不要买成品账号，不要外借，更不要代充。',
  },
  {
    titleParts: ['优先使用实体银行卡支付'],
    compactTitle: false,
    description: '使用本人在实体银行办理的信用卡或借记卡，姓名和账单地址按银行登记的信息填写。卡片归属地、发卡行、账单地址、注册时的 IP 和第一次连接时的 IP 要一一对应。不要使用加密货币交易所发行的卡，也不要找代充。如无外国银行卡，可考虑外区礼品卡付款，具体国家也要和账号、线路信息匹配。',
  },
  {
    titleParts: ['正常换 IP、换国家没问题'],
    compactTitle: false,
    description: '从风控角度考虑，用户出差、旅行、切换网络环境是可预期的正常行为。只要不是短期内频繁更换线路，通常不会单独导致封号。遇到断连时切换线路完全可以，偶尔换国家也没有问题。要避免的是短时间内在多个国家反复切换，同时频繁更换设备。',
  },
] as const

type Myth = {
  question: string
  answer: string
  linkLabel?: string
  linkTo?: string
  answerAfterLink?: string
}

const myths: readonly Myth[] = [
  {
    question: '用了美国住宅 IP，就不会被封号吗？',
    answer: '照样可能被封号。如上所述，平台的风控体系是多角度、多维度的。如果付款卡、账户资料没匹配上，或多人共用、异地登录，或出现其他高风险特征，即使使用住宅 IP 也会被封号。',
  },
  {
    question: '一定要用干净的独享 IP 才能不被封号？',
    answer: 'AI 平台从未公开也不可能公开其对 IP 的具体要求，“IP 是否干净”的定义或判定规则也没有统一标准。PandaFan 绝大多数用户都可以正常使用 AI 平台服务，说明我们的服务器并没有问题。如需要独享 IP，您可以购买 PandaFan 的【',
    linkLabel: '专线服务',
    linkTo: '/dedicated-servers',
    answerAfterLink: '】。专线仅供您个人/企业使用，速度快，服务稳。',
  },
  {
    question: '一定要连接美国服务器吗？',
    answer: '不需要。只要连接了平台支持的国家或地区的服务器就可以。事实上，如果您居住在东八区，台湾地区或日本的服务器可能更加契合您的作息和使用习惯。关键是整套资料一致，而不是非美国不可。',
  },
  {
    question: 'WebRTC 和 IPv6 必须全部关闭吗？',
    answer: 'IPv6 最好关闭，避免网络位置和当前连接的服务器不一致。各系统的关闭步骤见【',
    linkLabel: '关闭 IPv6 指南',
    linkTo: '/ipv6-guide',
    answerAfterLink: '】。WebRTC 没必要专门关闭，正常使用即可。',
  },
  {
    question: '一台电脑只能登录一个账号吗？',
    answer: '没有公开规则说一台电脑只能登录一个账号。但如果这台设备上已经有账号被封过，之后再登录其他账号需要更加谨慎，因为设备环境可能已经和之前的异常记录关联了。不过无论如何，个人账号与他人共用、短时间内登录很多账号，这些都会提高账户风险。',
  },
  {
    question: '人在国外、资料都是真实的，就一定不会被封号吗？',
    answer: '不能保证。即使是真实居住在国外、使用本人资料和正常付款方式的个人，也可能因为平台误判、异常登录、付款问题或规则变化被限制使用或封号。我们提供的这些使用建议只能减少信息冲突，但谁都不能保证账号永远不会被封。',
  },
  {
    question: 'Claude Pro 必须等两周才能升级 Max 吗？',
    answer: '没有这个官方要求。需要 Max 就按平台当时提供的选项升级，不用专门等够两周。',
  },
] as const

const checklist = [
  '平台支持当前连接的服务器所在地区',
  '系统时区、地区和语言与服务器所在地区一致',
  'PandaFan 已开启增强模式；保持智能分流即可，需要时也可以使用全局代理',
  '付款卡是本人在实体银行办理的，不是加密货币交易所发行的卡',
  '卡片归属地、发卡行、姓名、账单地址和首次连接地区互相匹配',
  '注册邮箱是自己正常使用的国外企业邮箱或常见海外邮箱，不是极冷门邮箱，也不是 QQ、163 等中国大陆邮箱',
  '账号、邮箱和手机号都是自己的',
  '个人账号没借给别人，也没和别人共用',
  '最近没有在短时间内反复切换多个国家，同时频繁更换系统设置或登录设备',
  '没买成品账号、没使用一次性手机号、没用来路不明的付款卡或代充',
] as const

useSeoMeta({
  title: 'AI服务使用指南｜PandaFan',
  description: 'AI 服务使用建议：风控不取决于单一因素，付款资料、账户信息、设备环境和使用习惯比 IP 更重要。',
  ogTitle: 'AI服务使用指南',
  ogDescription: '风控不取决于单一因素，付款资料、账户信息、设备环境和使用习惯比 IP 更重要。',
  twitterCard: 'summary_large_image',
})

useHead({
  title: 'AI服务使用指南｜PandaFan',
  htmlAttrs: {
    lang: 'zh-CN',
  },
})
</script>

<template lang="pug">
.ai-guide-shell
  a.skip-link(href="#guide-main") 跳到正文

  main#guide-main.ai-guide(tabindex="-1")
    header.guide-hero
      .hero-copy
        p.eyebrow PandaFan 使用建议 · 约 5 分钟读完
        h1
          span AI服务
          span 使用指南
        .hero-lead
          p PandaFan 可用于访问 ChatGPT、Gemini、Grok、Claude 等常见 AI 服务。
          p 完备风控模型通常是从多角度构建用户画像，不取决于单一因素。IP 在风控要素中较为边缘。更重要的因素包括：付款资料、账户信息、设备环境、使用习惯。只关注 IP 而忽略其他因素是舍本逐末，可能依然会被 AI 风控注意到。
        .hero-actions
          a.primary-action(href="#recommended-setup") 查看推荐设置
          a.secondary-action(href="#self-check") 用 1 分钟自查
          NuxtLink.secondary-action(to="/ipv6-guide") 关闭 IPv6 指南

      .ratio-panel(aria-label="PandaFan 的 AI 服务使用因素建议")
        p.ratio-title 风控不只看 IP
        .ratio-track(aria-hidden="true")
          .ratio-main
            span 付款资料 · 账户信息 · 设备环境 · 使用习惯
            strong 80%+
          .ratio-ip
            span 网络与 IP
            strong ＜20%

    .guide-layout
      aside.guide-sidebar
        nav(aria-label="本页目录")
          a(href="#what-matters")
            span 01
            | 核心因素
          a(href="#recommended-setup")
            span 02
            | 操作顺序
          a(href="#common-mistakes")
            span 03
            | 常见误区
          a(href="#self-check")
            span 04
            | 最后自查
          a(href="#what-we-can-do")
            span 05
            | 服务范围

      article.guide-content
        section#what-matters.guide-section(aria-labelledby="what-matters-title")
          .section-heading
            p.section-number 01 / 核心因素
            h2#what-matters-title 关键四因素

          ol.priority-list
            li.priority-item(v-for="item in priorities" :key="item.number")
              span.priority-number {{ item.number }}
              .priority-copy
                .priority-title-row
                  h3 {{ item.title }}
                  span.priority-level {{ item.level }}
                p(v-for="description in item.descriptions" :key="description") {{ description }}

        section#recommended-setup.guide-section(aria-labelledby="recommended-setup-title")
          .section-heading
            p.section-number 02 / 操作顺序
            h2#recommended-setup-title.punctuation-wrap
              span 第一次使用，
              wbr
              span 按这个顺序设置
            p 从选服务器到登录账号，一步一步来。设置好以后，平时不要反复改。

          ol.setup-list
            li.setup-item(v-for="(step, index) in setupSteps" :key="step.titleParts.join('')")
              span.setup-index {{ String(index + 1).padStart(2, '0') }}
              .setup-copy
                h3.punctuation-wrap(:class="{ 'compact-step-title': step.compactTitle }")
                  template(v-for="(part, partIndex) in step.titleParts" :key="part")
                    span {{ part }}
                    wbr(v-if="partIndex < step.titleParts.length - 1")
                p {{ step.description }}

          .claude-code-notice(role="note" aria-label="Claude Code 使用提醒")
            p.notice-label Claude Code 使用提醒
            h3.punctuation-wrap
              span 先连 PandaFan，
              wbr
              span 再开 Claude Code
            p Claude Code 在中国可以直接连接，所以一定不要把顺序反过来。开始时，先连接 PandaFan，再打开 Claude Code；结束时，先退出 Claude Code，再断开 PandaFan。否则 Claude 会记录到来自中国 IP 的直接连接。
            p ChatGPT 和 Codex 没有这个问题。

        section#common-mistakes.guide-section(aria-labelledby="common-mistakes-title")
          .section-heading
            p.section-number 03 / 常见误区
            h2#common-mistakes-title.compact-title 别把这些传言当成硬规则
            p 你可能经常看到这些说法，但平台并没有公开要求你这样做。

          .myth-list
            details.myth-item(v-for="myth in myths" :key="myth.question")
              summary
                span {{ myth.question }}
                span.summary-mark(aria-hidden="true") +
              p
                | {{ myth.answer }}
                NuxtLink.myth-link(v-if="myth.linkLabel && myth.linkTo" :to="myth.linkTo") {{ myth.linkLabel }}
                | {{ myth.answerAfterLink }}

        section#self-check.guide-section(aria-labelledby="self-check-title")
          .section-heading
            p.section-number 04 / 最后自查
            h2#self-check-title.punctuation-wrap
              span 花一分钟，
              wbr
              span 对一遍后再使用
            p 下面都对上以后，再打开 Claude、Codex 或 ChatGPT。平时尽量保持这些设置不变。

          ul.check-list
            li(v-for="item in checklist" :key="item")
              span.check-mark(aria-hidden="true") ✓
              span {{ item }}

        section#what-we-can-do.guide-section(aria-labelledby="what-we-can-do-title")
          .section-heading
            p.section-number 05 / 服务范围
            h2#what-we-can-do-title.compact-title 网络连接交给 PandaFan
            p PandaFan 不参与、也无法干预任何平台的账号风控、访问限制或封禁决策。

          .scope-grid
            .scope-column.scope-can
              p.scope-label PandaFan 能帮的
              ul
                li 提供稳定的网络连接与转发服务，减少意外断连
                li 针对 AI 平台优化线路，服务器节点覆盖多个主流平台支持国家和地区
                li 智能分流及时更新收录 AI 平台会用到的全部地址，省流更省心
                li 在使用出现连接问题时，提供必要的日志和排查支持
            .scope-column.scope-cannot
              p.scope-label AI 平台独立决定的
              ul
                li 注册、付款或订阅规则
                li 账号是否遇到额外验证、限制或停用
                li 具体的限制地区、用量及安全规则

        footer.guide-footer
          .footer-copy
            p.footer-title 现在，开始使用
          .footer-actions
            NuxtLink.primary-action(to="/download") 打开下载与使用指南

          .official-sources
            p 官方参考 · 页面更新于 2026 年 8 月 25 日
            .source-links
              a(href="https://openai.com/policies/terms-of-use/" target="_blank" rel="noopener noreferrer") OpenAI 使用条款
              a(href="https://www.anthropic.com/supported-countries" target="_blank" rel="noopener noreferrer") Anthropic 支持地区
              a(href="https://www.anthropic.com/transparency/system-trust-reporting" target="_blank" rel="noopener noreferrer") Anthropic 账号处理说明
</template>

<style lang="postcss" scoped>

.ai-guide-shell {
  --guide-paper: oklch(96.7% 0.014 102);
  --guide-surface: oklch(99.3% 0.008 101);
  --guide-ink: oklch(24% 0.028 139);
  --guide-muted: oklch(47% 0.024 139);
  --guide-line: oklch(83% 0.025 126);
  --guide-green: oklch(45% 0.13 139);
  --guide-green-soft: oklch(91% 0.09 127);
  --guide-lime: oklch(87% 0.18 126);
  --guide-warm: oklch(70% 0.12 75);
  width: 100%;
  align-self: stretch;
  color: var(--guide-ink);
  background: transparent;
}

.dark .ai-guide-shell {
  --guide-paper: oklch(15% 0.012 139);
  --guide-surface: oklch(19% 0.015 139);
  --guide-ink: oklch(94% 0.014 126);
  --guide-muted: oklch(72% 0.026 133);
  --guide-line: oklch(32% 0.026 139);
  --guide-green: oklch(77% 0.17 133);
  --guide-green-soft: oklch(29% 0.07 137);
  --guide-lime: oklch(80% 0.18 126);
  --guide-warm: oklch(78% 0.12 75);
}

.skip-link {
  position: fixed;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 80;
  padding: 0.75rem 1rem;
  color: var(--guide-paper);
  background: var(--guide-ink);
  transform: translateY(-150%);
  transition: transform 180ms cubic-bezier(0.25, 1, 0.5, 1);
}

.skip-link:focus {
  transform: translateY(0);
}

.ai-guide {
  width: var(--marketing-frame);
  margin: 0 auto;
  padding: clamp(2rem, 6vw, 6rem) 0 clamp(4rem, 8vw, 7rem);
}

.guide-hero {
  display: grid;
  gap: 3rem;
  padding-bottom: clamp(4rem, 8vw, 7rem);
  border-bottom: 1px solid var(--guide-line);
}

.hero-copy {
  max-width: 53rem;
  animation: guide-enter 600ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.eyebrow,
.section-number,
.ratio-title,
.scope-label {
  color: var(--guide-green);
  font-size: 0.78rem;
  font-weight: 750;
  letter-spacing: 0.12em;
}

.eyebrow {
  margin-bottom: 1.5rem;
}

h1,
h2,
h3,
p {
  margin: 0;
}

h1,
.footer-title {
  text-wrap: balance;
}

h1 {
  max-width: 12ch;
  font-family: 'Metropolis', 'SF Pro Display', 'PingFang SC', sans-serif;
  font-size: clamp(3.2rem, 9vw, 7.7rem);
  font-weight: 760;
  line-height: 0.94;
  letter-spacing: -0.065em;
}

h1 span {
  display: block;
}

.hero-lead {
  max-width: 42rem;
  margin-top: 2rem;
  color: var(--guide-muted);
  font-size: clamp(1.05rem, 1.4vw, 1.28rem);
  line-height: 1.85;
}

.hero-lead p + p {
  margin-top: 0.8rem;
}

.hero-actions,
.footer-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2rem;
}

.primary-action,
.secondary-action {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  justify-content: center;
  padding: 0.7rem 1rem;
  border: 1px solid var(--guide-ink);
  border-radius: 999px;
  color: var(--guide-ink);
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.2;
  text-decoration: none;
  transition:
    transform 160ms cubic-bezier(0.25, 1, 0.5, 1),
    color 160ms cubic-bezier(0.25, 1, 0.5, 1),
    background-color 160ms cubic-bezier(0.25, 1, 0.5, 1);
}

.primary-action {
  color: var(--guide-paper);
  background: var(--guide-ink);
}

.primary-action:focus-visible,
.secondary-action:focus-visible,
.guide-sidebar a:focus-visible,
.source-links a:focus-visible,
.myth-link:focus-visible,
.myth-item summary:focus-visible {
  outline: 3px solid var(--guide-lime);
  outline-offset: 3px;
}

.ratio-panel {
  align-self: end;
  animation: guide-enter 600ms 100ms cubic-bezier(0.16, 1, 0.3, 1) both;
}

.ratio-title {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 0.9rem;
  white-space: nowrap;
}

.ratio-title::after {
  width: 100%;
  border-top: 4px solid var(--guide-ink);
  content: '';
}

.ratio-track {
  display: grid;
  grid-template-columns: minmax(0, 4.2fr) minmax(8.25rem, 1fr);
  min-height: 9rem;
  border: 1px solid var(--guide-ink);
}

.ratio-main,
.ratio-ip {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
}

.ratio-main {
  color: oklch(18% 0.025 139);
  background: var(--guide-lime);
}

.ratio-ip {
  color: var(--guide-ink);
  background: var(--guide-surface);
  border-left: 1px solid var(--guide-ink);
}

.ratio-main span,
.ratio-ip span {
  font-size: 0.76rem;
  font-weight: 750;
  line-height: 1.35;
}

.ratio-main strong,
.ratio-ip strong {
  font-family: 'Metropolis', 'SF Pro Display', sans-serif;
  font-size: clamp(2.2rem, 6vw, 4.5rem);
  font-variant-numeric: tabular-nums;
  line-height: 0.9;
  letter-spacing: -0.06em;
}

.ratio-ip strong {
  font-size: clamp(1.75rem, 4vw, 3rem);
}

.guide-layout {
  display: grid;
  gap: 3rem;
  padding-top: clamp(3.5rem, 7vw, 6.5rem);
}

.guide-sidebar {
  display: none;
}

.guide-content {
  min-width: 0;
}

.guide-section {
  scroll-margin-top: 6.5rem;
}

.guide-section + .guide-section {
  margin-top: clamp(5rem, 10vw, 9rem);
}

.section-heading {
  max-width: 48rem;
  margin-bottom: 2.5rem;
}

.section-number {
  margin-bottom: 0.9rem;
}

.section-heading h2 {
  font-family: 'Metropolis', 'SF Pro Display', 'PingFang SC', sans-serif;
  font-size: clamp(2rem, 5vw, 4.25rem);
  font-weight: 740;
  line-height: 1.06;
  letter-spacing: -0.045em;
}

.punctuation-wrap > span {
  display: inline-block;
  white-space: nowrap;
}

.section-heading h2.compact-title {
  font-size: clamp(1.55rem, 7vw, 4rem);
}

.section-heading h2.compact-title:not(.punctuation-wrap) {
  white-space: nowrap;
}

.section-heading > p:last-child {
  max-width: 42rem;
  margin-top: 1.25rem;
  color: var(--guide-muted);
  font-size: 1rem;
  line-height: 1.8;
}

.priority-list,
.setup-list,
.check-list,
.scope-column ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.priority-list {
  border-top: 1px solid var(--guide-line);
}

.priority-item {
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr);
  gap: 1rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid var(--guide-line);
}

.priority-number,
.setup-index {
  color: var(--guide-green);
  font-family: 'Metropolis', 'SF Pro Display', sans-serif;
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
  font-weight: 750;
}

.priority-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem 1rem;
}

.priority-copy h3,
.setup-copy h3 {
  font-size: 1.12rem;
  font-weight: 750;
  line-height: 1.35;
}

.setup-copy h3.compact-step-title {
  font-size: clamp(1rem, 4.5vw, 1.12rem);
}

.priority-level {
  color: var(--guide-green);
  font-size: 0.76rem;
  font-weight: 750;
}

.priority-copy p,
.setup-copy p {
  max-width: 46rem;
  margin-top: 0.5rem;
  color: var(--guide-muted);
  line-height: 1.75;
}

.setup-list {
  display: grid;
  border-top: 1px solid var(--guide-line);
}

.setup-item {
  display: grid;
  grid-template-columns: 2.5rem minmax(0, 1fr);
  gap: 1rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid var(--guide-line);
}

.claude-code-notice {
  margin-top: 2rem;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  border: 2px solid var(--guide-green);
  background: var(--guide-green-soft);
}

.notice-label {
  margin-bottom: 0.8rem;
  color: var(--guide-green);
  font-size: 0.78rem;
  font-weight: 750;
  letter-spacing: 0.12em;
}

.claude-code-notice h3 {
  font-size: clamp(1.35rem, 2.5vw, 2rem);
  font-weight: 760;
  line-height: 1.25;
}

.claude-code-notice > p:not(.notice-label) {
  max-width: 48rem;
  margin-top: 0.8rem;
  line-height: 1.8;
}

.myth-list {
  border-top: 1px solid var(--guide-line);
}

.myth-item {
  border-bottom: 1px solid var(--guide-line);
}

.myth-item summary {
  display: flex;
  min-height: 4.75rem;
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1rem 0;
  font-size: clamp(1rem, 2vw, 1.2rem);
  font-weight: 720;
  list-style: none;
}

.myth-item summary::-webkit-details-marker {
  display: none;
}

.summary-mark {
  display: grid;
  width: 2rem;
  height: 2rem;
  flex: none;
  place-items: center;
  border: 1px solid var(--guide-line);
  color: var(--guide-green);
  font-size: 1.3rem;
  transition: transform 220ms cubic-bezier(0.65, 0, 0.35, 1);
}

.myth-item[open] .summary-mark {
  transform: rotate(45deg);
}

.myth-item > p {
  max-width: 48rem;
  padding: 0 3rem 1.5rem 0;
  color: var(--guide-muted);
  line-height: 1.8;
}

.myth-link {
  color: var(--guide-green);
  font-weight: 700;
  text-underline-offset: 0.18em;
}

.check-list {
  display: grid;
  gap: 0;
  border-top: 1px solid var(--guide-line);
}

.check-list li {
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr);
  gap: 0.8rem;
  align-items: start;
  padding: 1rem 0;
  border-bottom: 1px solid var(--guide-line);
  line-height: 1.65;
}

.check-mark {
  display: grid;
  width: 1.5rem;
  height: 1.5rem;
  place-items: center;
  color: oklch(18% 0.025 139);
  background: var(--guide-lime);
  font-size: 0.82rem;
  font-weight: 800;
}

.scope-grid {
  display: grid;
  border: 1px solid var(--guide-line);
}

.scope-column {
  padding: clamp(1.4rem, 4vw, 2.4rem);
  background: var(--guide-surface);
}

.scope-cannot {
  border-top: 1px solid var(--guide-line);
}

.scope-label {
  margin-bottom: 1.2rem;
}

.scope-column li {
  position: relative;
  padding-left: 1.25rem;
  color: var(--guide-muted);
  line-height: 1.7;
}

.scope-column li + li {
  margin-top: 0.85rem;
}

.scope-column li::before {
  content: '';
  position: absolute;
  top: 0.7em;
  left: 0;
  width: 0.4rem;
  height: 0.4rem;
  background: var(--guide-green);
}

.scope-cannot li::before {
  background: var(--guide-warm);
}

.guide-footer {
  margin-top: clamp(5rem, 10vw, 9rem);
  padding-top: 2rem;
  border-top: 4px solid var(--guide-ink);
}

.footer-copy {
  max-width: 48rem;
}

.footer-title {
  font-family: 'Metropolis', 'SF Pro Display', 'PingFang SC', sans-serif;
  font-size: clamp(1.8rem, 4vw, 3.5rem);
  font-weight: 740;
  line-height: 1.08;
  letter-spacing: -0.04em;
}

.footer-copy > p:last-child {
  margin-top: 0.8rem;
  color: var(--guide-muted);
  line-height: 1.7;
}

.official-sources {
  display: grid;
  gap: 0.8rem;
  margin-top: 3rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--guide-line);
  color: var(--guide-muted);
  font-size: 0.78rem;
  line-height: 1.6;
}

.source-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem 1.25rem;
}

.source-links a {
  color: var(--guide-green);
  font-weight: 700;
  text-underline-offset: 0.18em;
}

@media (hover: hover) {
  .primary-action:hover,
  .secondary-action:hover {
    transform: translateY(-2px);
  }

  .primary-action:hover {
    color: oklch(18% 0.025 139);
    background: var(--guide-lime);
  }

  .secondary-action:hover {
    color: var(--guide-paper);
    background: var(--guide-ink);
  }

  .guide-sidebar a:hover,
  .source-links a:hover,
  .myth-link:hover {
    color: var(--guide-ink);
  }
}

@media (min-width: 768px) {
  .setup-list,
  .check-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 2rem;
  }

  .setup-item,
  .check-list li {
    align-content: start;
  }

  .scope-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .scope-cannot {
    border-top: 0;
    border-left: 1px solid var(--guide-line);
  }

  .guide-hero {
    grid-template-columns: minmax(0, 1.3fr) minmax(17rem, 0.7fr);
    align-items: end;
  }

  .guide-layout {
    grid-template-columns: 8rem minmax(0, 1fr);
    gap: 2rem;
  }

  .guide-sidebar {
    position: sticky;
    top: 7rem;
    display: block;
    align-self: start;
  }

  .guide-sidebar nav {
    display: grid;
  }

  .guide-sidebar a {
    display: grid;
    grid-template-columns: 1.75rem minmax(0, 1fr);
    gap: 0.5rem;
    padding: 0.65rem 0;
    border-top: 1px solid var(--guide-line);
    color: var(--guide-muted);
    font-size: 0.82rem;
    font-weight: 650;
    line-height: 1.4;
    text-decoration: none;
    transition: color 160ms cubic-bezier(0.25, 1, 0.5, 1);
  }

  .guide-sidebar a:first-child {
    border-top: 0;
  }

  .guide-sidebar a span {
    color: var(--guide-green);
    font-variant-numeric: tabular-nums;
  }

  .priority-item {
    grid-template-columns: 3rem minmax(0, 1fr);
    padding-block: 1.8rem;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .guide-hero {
    gap: 2rem;
  }

  .ratio-main strong {
    font-size: clamp(2.2rem, 4vw, 2.6rem);
  }

  .ratio-ip strong {
    font-size: clamp(1.5rem, 2.7vw, 1.75rem);
  }

  .section-heading h2.compact-title {
    font-size: 2rem;
  }

  .setup-copy .punctuation-wrap > span {
    white-space: normal;
  }
}

@media (min-width: 1024px) {
  .guide-hero {
    grid-template-columns: minmax(0, 1.3fr) minmax(23rem, 0.7fr);
  }

  .guide-layout {
    grid-template-columns: 11rem minmax(0, 1fr);
    gap: clamp(3rem, 7vw, 7rem);
  }
}

@keyframes guide-enter {
  from {
    opacity: 0;
    transform: translateY(1.25rem);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-copy,
  .ratio-panel {
    animation: none;
  }

  .primary-action,
  .secondary-action,
  .summary-mark,
  .skip-link {
    transition-duration: 0.01ms;
  }
}
</style>
